import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listLibrary } from '@/api/library';
import { listOrders } from '@/api/orders';
import { Button } from '@/components/Button/Button';
import { LoginDialog } from '@/components/LoginDialog/LoginDialog';
import { PriceBreakdown } from '@/components/PriceBreakdown/PriceBreakdown';
import { Thumb } from '@/components/Thumb/Thumb';
import { useAuth } from '@/context/AuthContext';
import { formatPrice } from '@/utils/format';
import { lineBreakdown } from '@/utils/pricing';
import { paths } from '@/routes/paths';
import styles from './Orders.module.css';

/** "2 September 2026" — a purchase date only needs to be readable, not precise. */
function formatDate(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Everything this login has bought.
 *
 * Books live in playTUB, not here, so this is the receipt shelf rather than a
 * reader: what was bought, when, in which language editions, and whether playTUB
 * has been told about it. The list is scoped by the session token — there is no
 * user parameter to point somewhere else.
 */
export function Orders() {
  const { user, isSignedIn } = useAuth();
  // The book an expiry email sent the reader here to renew, marked on the page.
  const [searchParams] = useSearchParams();
  const renewTarget = searchParams.get('renew');

  const [orders, setOrders] = useState([]);
  const [state, setState] = useState('loading');
  const [signingIn, setSigningIn] = useState(false);
  /** book_id → where that book stands against its term, from the platform. */
  const [library, setLibrary] = useState({});

  const load = useCallback((signal) => {
    setState('loading');
    return listOrders({ signal })
      .then((result) => {
        setOrders(result);
        setState('ready');
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setState('error');
      });
  }, []);

  useEffect(() => {
    if (!isSignedIn) {
      setOrders([]);
      setState('anonymous');
      return undefined;
    }
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [isSignedIn, load]);

  // Read beside the orders, not before them: the receipts stand on their own,
  // and a platform that can't be reached costs the Renew buttons, not the page.
  useEffect(() => {
    if (!isSignedIn) {
      setLibrary({});
      return undefined;
    }
    const controller = new AbortController();
    // Keyed by lineage as well as by the version held: an order line names the
    // version that was on sale when it was bought, and the reader may have moved
    // to a newer one since.
    listLibrary({ signal: controller.signal })
      .then((entries) =>
        setLibrary(
          Object.fromEntries(
            entries.flatMap((e) => [
              [e.book_id, e],
              ...(e.lineage_id ? [[lineageKey(e.lineage_id), e]] : []),
            ]),
          ),
        ),
      )
      .catch(() => {});
    return () => controller.abort();
  }, [isSignedIn]);

  // A book can appear on several orders — bought, then renewed. It is one
  // entitlement, so every line of it says where it stands now; but the Renew
  // button, and the mark an expiry email asks for, go on its newest line only
  // (orders arrive newest first), so a renewed book is never offered twice.
  const newestLine = useMemo(() => {
    const lines = {};
    for (const order of orders) {
      order.items.forEach((item, index) => {
        const key = bookKey(item);
        if (item.format !== 'printed' && !(key in lines)) lines[key] = `${order.id}:${index}`;
      });
    }
    return lines;
  }, [orders]);

  // Arriving from an expiry email: bring the book it named into view.
  useEffect(() => {
    if (state !== 'ready' || !renewTarget) return;
    document.getElementById(`book-${renewTarget}`)?.scrollIntoView?.({ block: 'center' });
  }, [state, renewTarget]);

  if (!isSignedIn) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Your purchases</h1>
        <div className={styles.notice}>
          <p className={styles.noticeText}>
            Sign in to see the books you&apos;ve bought and the account they open on.
          </p>
          <Button onClick={() => setSigningIn(true)}>Sign in</Button>
        </div>

        <LoginDialog
          open={signingIn}
          reason="Sign in to see everything you've bought and open it in playTUB."
          onClose={() => setSigningIn(false)}
          onSuccess={() => setSigningIn(false)}
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Your purchases</h1>
      <div className={styles.subtitle}>
        Bought on <strong>{user.username}</strong> — open playTUB with the same login to read them.
      </div>

      {state === 'loading' && <div className={styles.notice}>Loading your purchases…</div>}

      {state === 'error' && (
        <div className={styles.notice}>
          <p className={styles.noticeText}>We couldn&apos;t load your purchases just now.</p>
          <Button onClick={() => load()}>Try again</Button>
        </div>
      )}

      {state === 'ready' && orders.length === 0 && (
        <div className={styles.notice}>
          <p className={styles.noticeText}>
            Nothing bought yet. Everything you buy shows up here, and in playTUB.
          </p>
          <Button variant="dark" to={paths.home}>
            Browse books →
          </Button>
        </div>
      )}

      {state === 'ready' && orders.length > 0 && (
        <ul className={styles.orders}>
          {orders.map((order) => (
            <li key={order.id} className={styles.order}>
              <div className={styles.orderHead}>
                <div>
                  <div className={styles.orderNo}>{order.id}</div>
                  <div className={styles.orderMeta}>
                    {formatDate(order.placed_at)}
                    {order.payment_summary && ` · ${order.payment_summary}`}
                  </div>
                </div>
                <div className={styles.orderTotal}>
                  {formatPrice(order.total, order.currency)}
                </div>
              </div>

              <ul className={styles.items}>
                {order.items.map((item, index) => {
                  const isNewest = newestLine[bookKey(item)] === `${order.id}:${index}`;
                  return (
                    <li key={`${order.id}-${item.id}-${item.languages?.join('+') ?? ''}`}>
                      <BookLine
                        item={item}
                        currency={order.currency}
                        reference={order.payment_reference}
                        standing={item.format !== 'printed' ? standingOf(library, item) : null}
                        isNewest={isNewest}
                        isTarget={isNewest && item.id === renewTarget}
                      />
                    </li>
                  );
                })}
              </ul>

              {order.entitlements_recorded === false && (
                <div className={styles.warning} role="status">
                  playTUB hasn&apos;t confirmed this one yet. Your payment went through — contact
                  support with order {order.id} if the books still aren&apos;t there.
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One purchased book: what it was, in which editions, and at what price. */
function BookLine({ item, currency, reference, standing, isNewest, isTarget }) {
  return (
    <div
      className={`${styles.item} ${isTarget ? styles.itemTarget : ''}`}
      id={isNewest ? `book-${item.id}` : undefined}
    >
      <Thumb letter={(item.name ?? '?').charAt(0)} src={item.image_url} size="xs" />
      <div className={styles.itemBody}>
        {/* Books can be withdrawn after purchase; the receipt still names them,
            but only a book still in the catalog has a page to link to. */}
        <Link to={paths.product(item.id)} className={styles.itemName}>
          {item.name}
        </Link>
        <PriceBreakdown item={item} currency={currency} />
        <div className={styles.itemMeta}>
          {item.kind === 'renewal'
            ? 'Renewal · one more term in playTUB'
            : item.kind === 'add_languages'
              ? 'Added language · for the rest of the term'
              : item.format === 'printed'
                ? 'Printed copy'
                : 'Digital · reads in playTUB'}
          {item.qty > 1 && ` · ×${item.qty}`}
        </div>
        {standing && (
          <Standing
            entitlement={standing}
            bookId={item.id}
            reference={reference}
            isNewest={isNewest}
          />
        )}
      </div>
      <div className={styles.itemPrice}>
        {formatPrice(lineBreakdown(item).lineTotal, item.currency ?? currency)}
      </div>
    </div>
  );
}

/**
 * Until when the book can be read, and — from a month before that, or once it has
 * ended — the way to keep it. Renewing early loses nothing: the new term starts
 * when the current one ends.
 */
function Standing({ entitlement, bookId, reference, isNewest }) {
  // An earlier line of a renewed book: the term THAT payment bought, rather than
  // the one now running. Only while a later term exists — a line whose payment
  // the platform has no later term after falls back to the book's own standing.
  // Grey either way: the book itself is still in the library, carried on by the
  // renewal, and a red "Expired" would say otherwise.
  const terms = entitlement.terms ?? [];
  const termIndex = isNewest ? -1 : terms.findIndex((t) => t.payment_reference === reference);
  if (termIndex >= 0 && termIndex < terms.length - 1) {
    const term = terms[termIndex];
    const ends = formatDate(term.expiry_date);
    return (
      <div className={styles.standing}>
        <span className={`${styles.standingLabel} ${styles.standingLive}`}>
          {term.ended ? `Expired on ${ends}` : `Term ends ${ends} · renewed`}
        </span>
      </div>
    );
  }

  const until = formatDate(entitlement.expiry_date);
  const label = entitlement.is_expired
    ? `Expired${until ? ` on ${until}` : ''}`
    : until
      ? `${entitlement.renewable ? 'Expires' : 'Access until'} ${until}`
      : 'In your library';
  const tone = entitlement.is_expired
    ? styles.standingEnded
    : entitlement.renewable
      ? styles.standingEnding
      : styles.standingLive;

  return (
    <div className={styles.standing}>
      <span className={`${styles.standingLabel} ${tone}`}>{label}</span>
      {entitlement.renewable && isNewest && (
        // Renewed from the version on sale: an older one is no longer listed.
        <Link to={paths.renew(entitlement.latest_book_id ?? bookId)} className={styles.renewBtn}>
          Renew subscription
        </Link>
      )}
      {entitlement.update_available && isNewest && (
        <span className={`${styles.standingLabel} ${styles.standingLive}`}>
          Version {entitlement.latest_version} is out · upgrade free in playTUB
        </span>
      )}
    </div>
  );
}

/** A library key for a book across its versions. */
function lineageKey(lineageId) {
  return `lineage:${lineageId}`;
}

/**
 * Which book an order line is, across versions. Lines bought before versions
 * existed carry no lineage — their book is the first version, whose id IS the
 * lineage.
 */
function bookKey(item) {
  return item.lineage_id ?? item.id;
}

/** The reader's purchase an order line belongs to, whichever version it holds now. */
function standingOf(library, item) {
  return library[item.id] ?? library[lineageKey(bookKey(item))] ?? null;
}
